<script setup>
import { onMounted, ref, watch, onBeforeUnmount, nextTick } from 'vue';
import { getChartThemeColors } from '../helpers/reportChartTheme';

const props = defineProps({
  // Object keyed by channel_type (e.g. 'Channel::WebWidget') ->
  // { '0-1h': n, '1-4h': n, '4-8h': n, '8-24h': n, '24h+': n }
  distribution: {
    type: Object,
    default: () => ({}),
  },
  title: {
    type: String,
    default: '',
  },
  emptyLabel: {
    type: String,
    default: '',
  },
});

// Fixed bucket order matching the backend builder.
const BUCKETS = ['0-1h', '1-4h', '4-8h', '8-24h', '24h+'];

// Palette reused from the other Kanban report charts (slate/woot tones).
const PALETTE = [
  '#1f93ff',
  '#10b981',
  '#8b5cf6',
  '#f59e0b',
  '#ef4444',
  '#06b6d4',
  '#ec4899',
  '#64748b',
];

const chartRef = ref(null);
let chartInstance = null;

const humanizeChannel = channelType =>
  String(channelType || '').replace('Channel::', '') || channelType;

const hasData = () =>
  props.distribution && Object.keys(props.distribution).length > 0;

const buildDatasets = () =>
  Object.keys(props.distribution).map((channelType, index) => {
    const buckets = props.distribution[channelType] || {};
    return {
      label: humanizeChannel(channelType),
      data: BUCKETS.map(bucket => Number(buckets[bucket] || 0)),
      backgroundColor: PALETTE[index % PALETTE.length],
      borderRadius: 6,
      barPercentage: 0.8,
      categoryPercentage: 0.7,
    };
  });

const destroyChart = () => {
  if (chartInstance) {
    chartInstance.destroy();
    chartInstance = null;
  }
};

const renderChart = async () => {
  await nextTick();
  destroyChart();
  if (!chartRef.value || !hasData()) return;
  const ctx = chartRef.value.getContext && chartRef.value.getContext('2d');
  if (!ctx) return;
  const Chart = (await import('chart.js/auto')).default;
  const theme = getChartThemeColors();
  chartInstance = new Chart(ctx, {
    type: 'bar',
    data: {
      labels: BUCKETS,
      datasets: buildDatasets(),
    },
    options: {
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          labels: { color: theme.legend, font: { size: 11 }, boxWidth: 12 },
        },
        tooltip: {
          backgroundColor: theme.tooltipBg,
          titleColor: theme.tooltipText,
          bodyColor: theme.tooltipText,
          callbacks: {
            label: context => `${context.dataset.label}: ${context.parsed.y}`,
          },
        },
      },
      scales: {
        x: {
          ticks: { color: theme.tick, font: { size: 11 } },
          grid: { display: false },
        },
        y: {
          beginAtZero: true,
          ticks: { color: theme.tick, font: { size: 11 }, precision: 0 },
          grid: { color: theme.grid },
        },
      },
      responsive: true,
      maintainAspectRatio: false,
    },
  });
};

onMounted(renderChart);
watch(() => props.distribution, renderChart, { deep: true });
onBeforeUnmount(destroyChart);
</script>

<template>
  <div
    class="bg-n-solid-1 rounded-2xl border border-n-weak p-3"
  >
    <h3
      class="text-[11.5px] font-bold text-n-slate-10 uppercase tracking-wide mb-2"
    >
      {{ title }}
    </h3>
    <div
      v-if="distribution && Object.keys(distribution).length > 0"
      style="height: 260px; width: 100%"
    >
      <canvas ref="chartRef"></canvas>
    </div>
    <div v-else class="text-center py-6 text-n-slate-10">
      <p class="text-sm">{{ emptyLabel }}</p>
    </div>
  </div>
</template>
